import {createSlice, PayloadAction} from "@reduxjs/toolkit"
import {AppThunk} from "../../rootReducer"
import {getAccessToken} from "../../utils/common"
import httpService from "../../utils/httpService"
import {setToastr} from "../App/appSlice"
import {Customer} from "../Customer/customerSlice"

// create type CustomPrice
export interface CustomPrice {
    customPricingID: number
    customPricingName: string
    days: string
    active: boolean
    monday: number | null
    tuesday: number | null
    wednesday: number | null
    thursday: number | null
    friday: number | null
    saturday: number | null
    sunday: number | null
    pricingCustomers: string[]
    pricingTestPackages: string[]
    regionId: number
    updatedDate: Date | null
    updatedBy: string
}

export interface CustomPricingTestPackage {
    testPackageID: number
    testPackageDescription: string
    testPackageName: string
    dealerID: number
    dealerName: string
    laneFee: number
    notes: string
    updatedByAppUserID: string
    updatedDate: Date
    active: boolean
    duration: number
    testFee: number
    effectiveDate: Date
    selected: boolean
}

export interface CustomTestPackage {
    dealerName: string
    dealerID: number
    customPricingTestPackages: CustomPricingTestPackage[]
}

type CustomPricesState = {
    customPrices: CustomPrice[]
    isCustomPriceLoading: boolean
    isModalLoading: boolean
    selectedCustomPrice: CustomPrice | null
    customPriceLoadingID: number | null
    selectedCustomPricingId: number
    selectedDealers: {
        [id: string]: boolean | number[]
    }
    selectedRegionId: number
    showManageDealers: boolean
    showAddEditCustomers: boolean
    showAddDealers: boolean
    showManageCustomPricing: boolean
    customPriceCustomers: Customer[]
    customPriceCustomersToAdd: Customer[]
    customPriceTestPackages: CustomTestPackage[]
    addCustomerLoader: boolean
    removeCustomerLoader: boolean
    selectedCustomPriceName: string
    showDeleteModal: boolean
    selectedCustomPriceDeleteId: number
    showDeleteLoader: boolean
}

const initialState: CustomPricesState = {
    customPrices: [],
    isCustomPriceLoading: false,
    isModalLoading: false,
    selectedCustomPrice: null,
    customPriceLoadingID: null,
    selectedCustomPricingId: 0,
    selectedDealers: {},
    selectedRegionId: 0,
    showManageDealers: false,
    showAddEditCustomers: false,
    showAddDealers: false,
    showManageCustomPricing: false,
    customPriceCustomers: [],
    customPriceCustomersToAdd: [],
    customPriceTestPackages: [],
    addCustomerLoader: false,
    removeCustomerLoader: false,
    selectedCustomPriceName: "",
    showDeleteModal: false,
    selectedCustomPriceDeleteId: 0,
    showDeleteLoader: false,
}

const customPricesSlice = createSlice({
    name: "customPrices",
    initialState,
    reducers: {
        setSelectedCustomPricingId(state, action: PayloadAction<number>) {
            state.selectedCustomPricingId = action.payload
        },
        setSelectedCustomPricingName(state, action: PayloadAction<string>) {
            state.selectedCustomPriceName = action.payload
        },
        setSelectedRegionId(state, action: PayloadAction<number>) {
            state.selectedRegionId = action.payload
        },
        setCustomPrices(state, action: PayloadAction<CustomPrice[]>) {
            state.customPrices = action.payload
        },
        setCustomPricesAreLoading(state, action: PayloadAction<boolean>) {
            state.isCustomPriceLoading = action.payload
        },
        setCustomPriceModalIsLoading(state, action: PayloadAction<boolean>) {
            state.isModalLoading = action.payload
        },
        setCustomPrice(state, action: PayloadAction<CustomPrice | null>) {
            state.selectedCustomPrice = action.payload
        },
        setCustomPriceCustomers(state, action: PayloadAction<Customer[]>) {
            state.customPriceCustomers = action.payload
        },
        setCustomPriceCustomersToAdd(state, action: PayloadAction<Customer[]>) {
            state.customPriceCustomersToAdd = action.payload
        },
        setCustomPriceTestPackages(state, action: PayloadAction<CustomTestPackage[]>) {
            const testpackagedata = action.payload
            const data = testpackagedata.map((x: any) => {
                x.expanded = false
                return x
            })

            state.customPriceTestPackages = data
        },
        setCustomPriceTestPackageModel(state, action: PayloadAction<any>) {
            let newtestmodal = state.customPriceTestPackages.map((x: any) => {
                if (x.dealerID === action.payload.dealerID) {
                    return {
                        ...x,
                        expanded: true,
                        customPricingTestPackages: action.payload.updatedTestPackages,
                    }
                } else {
                    return x
                }
            })
            state.customPriceTestPackages = newtestmodal
        },
        //AddEditModel
        showAddEditCustomersModal(state, action: PayloadAction<boolean>) {
            state.showAddEditCustomers = action.payload
        },
        showManageDealersModal(state, action: PayloadAction<boolean>) {
            state.showManageDealers = action.payload
        },
        showAddDealersModal(state, action: PayloadAction<boolean>) {
            state.showAddDealers = action.payload
        },
        showManageCustomPricingModal(state, action: PayloadAction<boolean>) {
            state.showManageCustomPricing = action.payload
        },
        setCustomPriceLoadingId(state, action: PayloadAction<number | null>) {
            state.customPriceLoadingID = action.payload
        },

        setAddCustomerLoader(state, action: PayloadAction<boolean>) {
            state.addCustomerLoader = action.payload
        },
        setRemoveCustomerLoader(state, action: PayloadAction<boolean>) {
            state.removeCustomerLoader = action.payload
        },
        showDeleteModal(state, action: PayloadAction<boolean>) {
            state.showDeleteModal = action.payload
        },
        setDeleteCustomPriceId(state, action: PayloadAction<number>) {
            state.selectedCustomPriceDeleteId = action.payload
        },
        setShowDeleteLoader(state, action: PayloadAction<boolean>) {
            state.showDeleteLoader = action.payload
        },
    },
})

export const {
    setCustomPrices,
    setCustomPricesAreLoading,
    setCustomPriceModalIsLoading,
    setCustomPrice,
    setCustomPriceLoadingId,
    setCustomPriceTestPackageModel,
    showAddEditCustomersModal,
    setCustomPriceCustomers,
    setSelectedCustomPricingId,
    setCustomPriceCustomersToAdd,
    showManageDealersModal,
    showAddDealersModal,
    setCustomPriceTestPackages,
    setSelectedRegionId,
    showManageCustomPricingModal,
    setAddCustomerLoader,
    setRemoveCustomerLoader,
    setSelectedCustomPricingName,
    showDeleteModal,
    setDeleteCustomPriceId,
    setShowDeleteLoader,
} = customPricesSlice.actions

export default customPricesSlice.reducer

export const fetchCustomPrice =
    (selectedRegionId: number, selectedCustomPriceId: number): AppThunk =>
    (dispatch: any, getState) =>
        httpService(getAccessToken(getState()))
            .get(`regions/${selectedRegionId}/customprice/${selectedCustomPriceId}`)
            .then(res => dispatch(setCustomPrice(res.data)))
            .catch(error => dispatch(setCustomPrice(null)))

export const fetchCustomPrices =
    (selectedRegionId: number): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setCustomPricesAreLoading(true))
        httpService(getAccessToken(getState()))
            .get(`regions/${selectedRegionId}/customprices`)
            .then(res => {
                dispatch(setSelectedRegionId(selectedRegionId))
                dispatch(setCustomPrices(res.data))
            })
            .catch(error => {
                dispatch(setSelectedRegionId(selectedRegionId))
                dispatch(setCustomPrices([]))
            })
            .finally(() => dispatch(setCustomPricesAreLoading(false)))
    }

export const fetchCustomPricingCustomers =
    (selectedRegionId: number, customPricingId: number): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setRemoveCustomerLoader(true))

        httpService(getAccessToken(getState()))
            .get(`regions/${selectedRegionId}/customprice-existingcustomers/${customPricingId}`)
            .then(res => dispatch(setCustomPriceCustomers(res.data)))
            .catch(error => dispatch(setCustomPriceCustomers([])))
            .finally(() => {
                dispatch(setRemoveCustomerLoader(false))
            })
    }

export const fetchCustomPricingCustomersToAdd =
    (selectedRegionId: number, customPricingId: number, searchText: string): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setAddCustomerLoader(true))
        httpService(getAccessToken(getState()))
            .get(
                `regions/${selectedRegionId}/customprice-customerstoadd/${customPricingId}?filterText=${encodeURIComponent(
                    searchText,
                )}`,
            )
            .then(res => {
                dispatch(setCustomPriceCustomersToAdd(res.data))
            })
            .catch(error => {
                dispatch(setCustomPriceCustomersToAdd([]))
            })
            .finally(() => {
                dispatch(setAddCustomerLoader(false))
            })
    }

export const fetchCustomPricingTestPackages =
    (selectedRegionId: number, customPricingId: number): AppThunk =>
    (dispatch: any, getState) => {
        return httpService(getAccessToken(getState()))
            .get(`regions/${selectedRegionId}/customprice-existingtestpackages/${customPricingId}`)
            .then(res => dispatch(setCustomPriceTestPackages(res.data)))
            .catch(error => dispatch(setCustomPriceTestPackages([])))
    }

export const addCustomPriceCustomers =
    (selectedRegionId: number, customPricingId: number, customerIds: string[], searchText: string): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setAddCustomerLoader(true))
        httpService(getAccessToken(getState()))
            .put(`regions/${selectedRegionId}/customprice-customerstoadd/${customPricingId}`, customerIds)
            .then(() => {
                dispatch(fetchCustomPrices(selectedRegionId))
                dispatch(fetchCustomPricingCustomers(selectedRegionId, customPricingId))
                dispatch(fetchCustomPricingCustomersToAdd(selectedRegionId, customPricingId, searchText))
            })
            .finally(() => {
                dispatch(setAddCustomerLoader(false))
            })
    }

export const removeCustomPriceCustomers =
    (selectedRegionId: number, customPricingId: number, customerIds: string[], searchText: string): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setRemoveCustomerLoader(true))
        httpService(getAccessToken(getState()))
            .delete(`regions/${selectedRegionId}/customprice-existingcustomers/${customPricingId}`, null, {
                data: customerIds,
            })
            .then(() => {
                dispatch(fetchCustomPrices(selectedRegionId))
                dispatch(fetchCustomPricingCustomers(selectedRegionId, customPricingId))
                dispatch(fetchCustomPricingCustomersToAdd(selectedRegionId, customPricingId, searchText))
            })
            .finally(() => {
                dispatch(setRemoveCustomerLoader(false))
            })
    }

export const postCustomPriceDetails =
    (selectedRegionId: number, customPrice: CustomPrice): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setCustomPriceModalIsLoading(true))
        const pricingId = customPrice.customPricingID
        customPrice.regionId = selectedRegionId
        if (!pricingId || pricingId === 0) {
            // add
            customPrice.customPricingID = 0
            httpService(getAccessToken(getState()))
                .put(`regions/${selectedRegionId}/customprices`, customPrice)
                .then(() => {
                    dispatch(fetchCustomPrices(selectedRegionId))
                    dispatch(showManageCustomPricingModal(false))
                    dispatch(setSelectedCustomPricingId(0))
                })
                .catch(error =>
                    dispatch(
                        setToastr({
                            isActive: true,
                            message: error.response.data.Value.Error,
                            delay: 3000,
                            title: "Error",
                            type: "danger",
                            description: "",
                        }),
                    ),
                )
                .finally(() => {
                    dispatch(setCustomPriceModalIsLoading(false))
                })
        } else {
            // update
            httpService(getAccessToken(getState()))
                .post(`regions/${selectedRegionId}/customprices/${pricingId}`, customPrice)
                .then(() => {
                    dispatch(fetchCustomPrices(selectedRegionId))
                    dispatch(showManageCustomPricingModal(false))
                    dispatch(setSelectedCustomPricingId(0))
                })
                .catch(error =>
                    dispatch(
                        setToastr({
                            isActive: true,
                            message: error.response.data.Value.Error,
                            delay: 3000,
                            title: "Error",
                            type: "danger",
                            description: "",
                        }),
                    ),
                )
                .finally(() => {
                    dispatch(setCustomPriceModalIsLoading(false))
                })
        }
    }

export const updateCustomPricesActiveStatus =
    (selectedRegionId: number, customPricingId: number, isActive: boolean): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setCustomPriceLoadingId(customPricingId))
        httpService(getAccessToken(getState()))
            .put(`regions/${selectedRegionId}/customprices/${customPricingId}`, isActive)
            .then((res: {data: boolean}) => {
                dispatch(
                    setCustomPrices(
                        getState().containers.customPrices.customPrices.map(el =>
                            el.customPricingID === customPricingId ? {...el, active: res.data} : el,
                        ),
                    ),
                )
                dispatch(fetchCustomPrices(selectedRegionId))
                dispatch(setCustomPriceLoadingId(null))
            })
    }

export const deleteCustomPrice =
    (customPricingId: number, selectedRegionId: number): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setShowDeleteLoader(true))
        httpService(getAccessToken(getState()))
            .delete(`regions/${selectedRegionId}/customprice/${customPricingId}`)
            .then(() => {
                dispatch(setShowDeleteLoader(false))
                dispatch(fetchCustomPrices(selectedRegionId))
                dispatch(showDeleteModal(false))
                dispatch(
                    setToastr({
                        isActive: true,
                        message: "Successfully Deleted",
                        delay: 3000,
                        title: "Success",
                        type: "light",
                        description: "",
                    }),
                )
            })
            .catch(error => {
                dispatch(
                    setToastr({
                        isActive: true,
                        message: error.response.data.Value.Error,
                        delay: 3000,
                        title: "Error",
                        type: "danger",
                        description: "",
                    }),
                )
            })
    }
